import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom-v5-compat'

import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'

import { api } from '~/api'
import QueryMatcher from '~/components/QueryMatcher'
import GiftWithPurchaseForm from '~/promotions/components/GiftWithPurchaseForm'
import { giftWithPurchaseQueryKeys } from '~/promotions/queries/queryKeys'
import type { GiftWithPurchaseCampaignDetail } from '~/promotions/schemas'

export default function GiftWithPurchaseEditPage() {
    const queryClient = useQueryClient()
    const { t } = useTranslation(['common', 'gift_with_purchase'])
    const { enqueueSnackbar } = useSnackbar()
    const { campaign_id } = useParams()
    const id = Number(campaign_id)

    const giftWithPurchaseCampaignDetailQuery = api.promotions.getGiftWithPurchaseCampaign.useQuery(
        giftWithPurchaseQueryKeys.detail(id),
        {
            params: {
                id,
            },
        }
    )

    const { mutate: updateGiftWithPurchaseCampaign, isLoading: isUpdating } =
        api.promotions.updateGiftWithPurchaseCampaign.useMutation()

    const handleSubmit = useCallback(
        (body: GiftWithPurchaseCampaignDetail) => {
            return updateGiftWithPurchaseCampaign(
                {
                    body,
                    params: { id },
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: giftWithPurchaseQueryKeys.detail(id),
                        })
                        enqueueSnackbar(t('gift_with_purchase:page_edit.request_success'), {
                            variant: 'success',
                        })
                    },
                    onError: () => {
                        enqueueSnackbar(t('gift_with_purchase:page_edit.request_failed'), {
                            variant: 'error',
                        })
                    },
                }
            )
        },
        [id, queryClient, updateGiftWithPurchaseCampaign, enqueueSnackbar, t]
    )

    return (
        <QueryMatcher
            query={giftWithPurchaseCampaignDetailQuery}
            Loading={<GiftWithPurchaseForm.Skeleton />}
            Errored={(error) => (
                <Typography variant="subtitle2" color="error">
                    {error instanceof Error ? error.message : 'Something went wrong'}
                </Typography>
            )}
        >
            {({ data }) => {
                if (!data?.body) {
                    throw new Error('Internal server error. No data returned.')
                }

                return (
                    <GiftWithPurchaseForm
                        type="edit"
                        defaultValues={data.body}
                        isSubmitting={isUpdating}
                        onSubmit={handleSubmit}
                    />
                )
            }}
        </QueryMatcher>
    )
}
